import React, { useState } from 'react';
import CaseTypeListHeader from '../ListHeader/CaseTypeListHeader';
import CaseTypeListItem from './CaseTypeListItem';
import useCaseTypeTree, {
	CaseTypeTreeItem,
} from '../../../Hooks/useCaseTypeTree';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import CaseTypeOrganizationUnitModal from '../OrganizationUnit/CaseTypeOrganizationUnitModal';
import { CaseTypeType } from '../../../Enums/CaseTypeType.enum';
import styles from './CaseTypeList.module.scss';
import CaseTypeListStripeNotification from './CaseTypeListStripeNotification';
import CaseTypeWizardModal from '../CaseTypeWizard/CaseTypeWizardModal';
import Text from '../../../../../Components/Base/Text/Text';

interface Props {
	workplaceId: string;
	healthPlanCode?: string;
	openShifts: () => void;
}

const CaseTypeList: React.FC<Props> = ({
	workplaceId,
	healthPlanCode,
	openShifts,
}) => {
	const { caseTypeTree, isOnlyOneRoot, reFetch, isLoading } = useCaseTypeTree(
		workplaceId,
		healthPlanCode,
	);

	const [caseTypeId, setCaseTypeId] = useState<string | undefined>(undefined);
	const [organizationUnitId, setOrganizationUnitId] = useState<
		string | undefined
	>(undefined);

	const handleOrganizationUnitEditSuccess = async () => {
		setOrganizationUnitId(undefined);
		await reFetch();
	};

	const handleCaseTypeEditSuccess = async () => {
		await reFetch();
	};

	const handleEdit = (item: CaseTypeTreeItem) => {
		if (item.caseType.type === CaseTypeType.CASE_TYPE) {
			setCaseTypeId(item.id);
		}
		if (item.caseType.type === CaseTypeType.FOLDER) {
			setOrganizationUnitId(item.id);
		}
	};

	const emptyMessageText = healthPlanCode
		? 'component.caseTypeList.emptyMessageHealthPlan'
		: 'component.caseTypeList.emptyMessage';

	return (
		<>
			<div className={styles.wrapper}>
				<CaseTypeListHeader
					workplaceId={workplaceId}
					reloadList={reFetch}
					isOnlyOneRoot={isOnlyOneRoot}
					openShifts={openShifts}
					isControlsEnabled={!healthPlanCode}
				/>
				<CaseTypeListStripeNotification />
				<div className={styles.list}>
					{isLoading && <OverlayLoading />}
					{caseTypeTree?.length > 0 ? (
						<ScrollContainer className={styles.scrollContainer}>
							{caseTypeTree?.map(item => (
								<React.Fragment key={item.id}>
									<CaseTypeListItem item={item} onEdit={handleEdit} />
								</React.Fragment>
							))}
						</ScrollContainer>
					) : (
						<Text tx={emptyMessageText} className={styles.empty} />
					)}
				</div>
			</div>
			{organizationUnitId && (
				<CaseTypeOrganizationUnitModal
					isOpened
					onClose={() => setOrganizationUnitId(undefined)}
					workplaceId={workplaceId}
					organizationUnitId={organizationUnitId}
					onSuccess={handleOrganizationUnitEditSuccess}
					isOnlyOneRoot={isOnlyOneRoot}
				/>
			)}
			{caseTypeId && (
				<CaseTypeWizardModal
					isOpen
					onClose={() => setCaseTypeId(undefined)}
					workplaceId={workplaceId}
					caseTypeId={caseTypeId}
					reloadList={handleCaseTypeEditSuccess}
					isOnlyOneRoot={isOnlyOneRoot}
					onRedirectToShifts={openShifts}
				/>
			)}
		</>
	);
};

export default CaseTypeList;
